/**
 * Declared merge strategy for the object arrays inside a farm policy segment.
 *
 * Purpose: farm segments store deltas, and every merge site needs to know which field identifies an
 * item in an array. That knowledge used to live in four hand-maintained lists written in four
 * different vocabularies — `normalizedProviderDataArrayKeys` (farm-utils read),
 * `FULL_REPLACE_ARRAY_FIELDS` (policy-mod delta write), `FE_AUTHORITATIVE_RISK_*_FIELDS` (rater-v2
 * offer write) and the `changesArray[0].id` sniff in `mergeArraysByIdOrReplace`. None of them was
 * checked against the schema, each guessed differently when a field was unlisted, and two of them
 * were near-verbatim copies of each other. A copy that got a fix while its twin did not is what
 * produced #34355 (and, before it, #29374, #31476, #31790, #32575 and #32576).
 *
 * Flow: `getArrayMergePolicy(fieldName)` is the single answer. `read` drives delta reconstruction in
 * `applyFarmObjectDelta`; `write` drives how `calculateSegmentDelta` stores a changed array.
 *
 * Key decision: the two axes are declared separately because they can be out of step. A
 * `write: 'full-array'` paired with a `read: 'legacy-id'` is precisely the list of pending
 * migrations. `scheduleCoverages` and `coverageF` were the last such pair: stored whole since #31790
 * but read id-keyed until #37140, which moved them to `replace-if-full-array` (see that kind below).
 *
 * Two known limits. Entries are keyed by bare field name, matching the existing dispatch in
 * `applyFarmObjectDelta`, so a name reachable under two parent types with different shapes gets one
 * strategy for both (`items` is the live case). And both post-`deepmerge` `feAuthoritative`
 * consumers scan `revisedRisk.building` only, so an entry for an array living on `coverages` or at
 * the risk root does nothing at those sites; only the by-name consumer (`mergeArraysByIdOrReplace`)
 * sees it. `scheduleCoverages` / `coverageF` live on `coverages` and get their post-`deepmerge`
 * assignment from dedicated code in policy-mod and rater-v2 instead.
 *
 * Issue: #34355. Prior art: #32575 (offer path), #31790, #32576, #31476.
 */

/**
 * - `replace`: the delta array is the whole list; assign it.
 * - `key`: merge entries into the base by `key`.
 * - `legacy-id`: merge entries into the base by `id` (an id-less entry matches base index 0).
 * - `replace-if-full-array`: for a field whose WRITE moved from a positional diff to the whole list,
 *   with no write-era marker on the segment. A delta made only of complete entries is assigned
 *   verbatim; one carrying the old diff's sparse entries or tombstones goes through `legacy-id`
 *   exactly as before. See `isFullArrayDelta` in farm-policy-utils for the rules and their limit.
 */
export type ArrayReadStrategy =
  | { kind: 'replace' }
  | { kind: 'key'; key: string }
  | { kind: 'legacy-id' }
  | { kind: 'replace-if-full-array'; requiredFields: readonly string[] };

export type ArrayMergePolicy = {
  write: 'full-array' | 'positional-diff';
  read: ArrayReadStrategy;
  /**
   * The FE owns this list and sends it complete, so a post-`deepmerge` fixup must assign it verbatim
   * instead of letting deepmerge-ts concatenate. Declared rather than derived from
   * `read: 'replace'`: those two facts coincide for mortgagees but are independent, and a
   * backend-computed array that wanted a wholesale read replace must not silently start being
   * overwritten from the FE payload.
   */
  feAuthoritative?: boolean;
  /**
   * `read` is known to mis-key this field's entries, so the merge gives a wrong answer today. Set
   * only with a tracking issue. This exists so "declare a strategy and move on" cannot quietly
   * satisfy the schema invariant test with the broken strategy — that test requires the marker
   * whenever a `legacy-id` read meets an element type the schema says has no `id`.
   */
  readKnownWrong?: boolean;
};

const DEFAULT_POLICY: ArrayMergePolicy = { write: 'positional-diff', read: { kind: 'legacy-id' } };

/** Normalized property-provider responses key their entries by `attribute`, not `id`. */
export const PROVIDER_DATA_FIELDS = [
  'branch',
  'cape',
  'cherre',
  'coreLogic',
  'input',
  'smartyStreets',
  'verisk',
  'veriskRoofYear'
];

const POLICY_BY_FIELD: Record<string, ArrayMergePolicy> = {
  // `MortgageDetails` carries no `id` in the Dynamo type, in either GraphQL schema, or in staff-fe's
  // `MortgageDetailsInput`, and staff-fe rewrites every mortgageable building's list on each save.
  // The submitted list is authoritative, so store it whole and replace on read (#34355).
  mortgageDetails: { write: 'full-array', read: { kind: 'replace' }, feAuthoritative: true },

  // Stored whole since #31790 (PR #31867, 2026-06-15) but read id-keyed until #37140. Schedule-coverage
  // entries have no `id`, so every delta entry landed on base index 0: an emptied list (`items: []`)
  // did not clear, a removed item or coverage type survived, and two coverage types merged into each
  // other — and the rater charged for what the agent had removed. coverageF items do carry an `id`,
  // but a whole-list write has no tombstones, so a removed item survived the id-keyed read the same
  // way. Segments written before #31867 hold positional diffs (sparse entries, `isDeleted`
  // tombstones), which `replace-if-full-array` recognises and reads id-keyed exactly as before.
  // A pre-#31867 bare append is the one historical shape it cannot recognise; see `isFullArrayDelta`.
  // `feAuthoritative` makes policy-mod's revise path (`mergeArraysByIdOrReplace`) replace the list
  // too, instead of unioning a removed coverage type back in by `coverageType`.
  scheduleCoverages: {
    write: 'full-array',
    read: { kind: 'replace-if-full-array', requiredFields: ['coverageType', 'items'] },
    feAuthoritative: true
  },
  // Every non-null field of `ScheduledFarmPropertyItemDetails` that rating needs. A pre-#31867 edit
  // stored `{ ...changedFields, id }`, so requiring all of them is what tells it apart from a whole item.
  coverageF: {
    write: 'full-array',
    read: {
      kind: 'replace-if-full-array',
      requiredFields: ['id', 'category', 'limit', 'description', 'deductible']
    },
    feAuthoritative: true
  },

  // `RiskClarionDoorMap` has no `id` (it is keyed by `riskId`), and the list is a computed snapshot
  // of the ClarionDoor request ids a rating produced — every rating path writes the whole
  // `FarmClarionDoorMap` (offer/bind, policy-mod `setSegmentValues`, renewals), so a re-rated
  // segment's list replaces the prior one rather than merging id-less entries onto index 0 (#35385).
  risksClarionDoorIds: { write: 'full-array', read: { kind: 'replace' } },

  // Declared, marked wrong, not yet migrated. Each element type has no `id`, so the id-keyed read
  // collapses every delta entry onto base index 0 — the same mechanism as #34355. Held back because
  // they feed rating or billing and their stored deltas are genuinely sparse, so each needs a
  // production scan and underwriting review first.
  ...Object.fromEntries(
    [
      'branchPlusPolicies',
      'businessActivities',
      'businessPursuits',
      'cars',
      'coverages',
      'customFarmWorkItems',
      'emails',
      'invited',
      // Reachable both id-less (scheduledPersonalPropertyDetails.items) and id-bearing
      // (FarmScheduledPropertyDetails.items). Keying by bare field name cannot tell them apart, so
      // this entry describes the id-less one and is harmless for the other.
      'items',
      'otherPropertyAddresses',
      'phoneNumbers',
      'priorCars',
      'rentalPropertyAddresses',
      'trailers'
    ].map((field) => [field, { write: 'positional-diff', read: { kind: 'legacy-id' }, readKnownWrong: true }])
  ),

  ...Object.fromEntries(
    PROVIDER_DATA_FIELDS.map((field) => [field, { write: 'positional-diff', read: { kind: 'key', key: 'attribute' } }])
  )
};

/** Every field name the registry declares explicitly. Drives the schema invariant test. */
export const REGISTERED_ARRAY_FIELD_NAMES: readonly string[] = Object.freeze(Object.keys(POLICY_BY_FIELD));

/** Fields whose declared read strategy is known to give a wrong answer, pending migration. */
export const KNOWN_WRONG_READ_FIELD_NAMES: readonly string[] = Object.freeze(
  Object.entries(POLICY_BY_FIELD)
    .filter(([, policy]) => policy.readKnownWrong === true)
    .map(([fieldName]) => fieldName)
);

export function getArrayMergePolicy(fieldName: string): ArrayMergePolicy {
  return POLICY_BY_FIELD[fieldName] ?? DEFAULT_POLICY;
}

/** Field names whose changed array must be stored complete rather than as a positional diff. */
export const FULL_REPLACE_ARRAY_FIELD_NAMES: readonly string[] = Object.freeze(
  Object.entries(POLICY_BY_FIELD)
    .filter(([, policy]) => policy.write === 'full-array')
    .map(([fieldName]) => fieldName)
);

/**
 * Array fields the FE owns outright: it sends the complete list, so a post-`deepmerge` fixup must
 * assign it verbatim rather than let deepmerge-ts concatenate. Both the offer updater and the
 * policy-mod endorsement updater read this, so they cannot drift apart again.
 */
export const FE_AUTHORITATIVE_ARRAY_FIELD_NAMES: readonly string[] = Object.freeze(
  Object.entries(POLICY_BY_FIELD)
    .filter(([, policy]) => policy.feAuthoritative === true)
    .map(([fieldName]) => fieldName)
);

/**
 * Assign every FE-authoritative array the payload carries, overriding whatever `deepmerge` produced.
 *
 * Both the offer updater and the policy-mod endorsement updater call this, so they share the
 * *logic* and not merely the field list — sharing only the list still let a `null` guard land on one
 * copy and not its twin, which is the same drift that produced #34355.
 *
 * Presence, not truthiness: an empty list means "remove them all". `null` normalises to empty so the
 * preview and the stored policy cannot disagree. Scalars are deliberately not handled here — the
 * removable-coverage fields alongside these arrays must keep assigning `null` verbatim.
 *
 * `hasOwnProperty` rather than `Object.hasOwn`: policy-mod pins es2021.
 */
export function applyFeAuthoritativeArrayFields(
  target: Record<string, any> | null | undefined,
  submitted: Record<string, any> | null | undefined
): void {
  if (!target || !submitted) {
    return;
  }

  for (const field of FE_AUTHORITATIVE_ARRAY_FIELD_NAMES) {
    if (Object.prototype.hasOwnProperty.call(submitted, field)) {
      target[field] = submitted[field] ?? [];
    }
  }
}
